/** Storage key for the region the current application belongs to. */
const REGION_STORAGE_KEY = 'pet_app_region';

/**
 * Reads the region of the current application, normalised to a lowercase code.
 * @returns the region code (e.g. 'qld'), or null when none has been established yet.
 */
export const getAppRegion = (): string | null => {
  const region = localStorage.getItem(REGION_STORAGE_KEY);
  return region?.trim().toLowerCase() || null;
};

/**
 * Records the region of the current application.
 *
 * Only writes when the caller actually has a value. A missing region means "we
 * were not told" — not "this is NSW". Clearing it there silently downgrades a
 * QLD application to NSW behaviour for the rest of the session, because every
 * consumer falls back to NSW when the key is absent.
 *
 * @param region region code from the API; null/undefined leaves the stored value untouched.
 */
export const setAppRegion = (region?: string | null): void => {
  const normalised = region?.trim().toLowerCase();
  if (!normalised) {
    return;
  }
  localStorage.setItem(REGION_STORAGE_KEY, normalised);
};

/**
 * Checks if the current region is QLD.
 * @returns true if the region is QLD, false otherwise
 */
export const isQldRegion = (): boolean => getAppRegion() === 'qld';

/**
 * Regions for which the tenant landing page (pre-form intro screen) is enabled.
 * Add/remove a region code here to toggle the landing for that region — no other change needed.
 */
export const TENANT_LANDING_REGIONS = ['qld', 'nsw'];

/**
 * Whether the tenant landing page should be shown for the current region.
 * @returns true if the current region is in TENANT_LANDING_REGIONS.
 */
export const shouldShowTenantLanding = (): boolean => {
  const region = getAppRegion();
  return !!region && TENANT_LANDING_REGIONS.includes(region);
};

/**
 * Generates the profile title for a pet.
 * Shows "Type Index" format (e.g., "Dog 1", "Cat 2").
 *
 * @param type - The pet type (dog, cat, fish, etc.)
 * @param index - The 0-based index of the pet within its type
 * @returns The formatted profile title
 */
export const getProfileTitle = (type: string, index: number): string => {
  // Format the type name properly
  let formattedType = type.charAt(0).toUpperCase() + type.slice(1);
  if (formattedType === "SmallMammal") {
    formattedType = "Small Mammal";
  } else if (formattedType === "Other") {
    formattedType = "Other Animal";
  }

  return `${formattedType} ${index + 1}`;
};

/**
 * Returns pet input fields by pet UUID.
 * @param formData - Nested record of form data
 * @param items - Array of pet items
 * @param petUuid - Pet UUID to search for
 */
export const getPetInputByUuid = (
  formData: Record<string, Record<string, Record<number, any>>>,
  items: Array<{ pet_id: string; type: string; uid: number }>,
  petUuid: string
) => {
  const item = items.find((i) => i.pet_id === petUuid);
  if (!item) return null;
  const { type, uid } = item;
  const petFields: Record<string, any> = {};
  Object.keys(formData[type] || {}).forEach((field) => {
    if (formData[type][field][uid] !== undefined) petFields[field] = formData[type][field][uid];
  });
  return petFields;
};